import React, { useState } from 'react'
import { toast } from 'react-toastify'
import api from '../../services/api'

const DEFAULT_COLORS = [
  '#dc3545', '#28a745', '#007bff', '#ffc107', '#6f42c1',
  '#17a2b8', '#fd7e14', '#20c997', '#e83e8c', '#6c757d'
]

const DEFAULT_ICONS = [
  'fas fa-microphone', 'fas fa-broadcast-tower', 'fas fa-headphones',
  'fas fa-tv', 'fas fa-video', 'fas fa-camera', 'fas fa-edit',
  'fas fa-phone', 'fas fa-cogs', 'fas fa-tools', 'fas fa-users',
  'fas fa-desktop', 'fas fa-server', 'fas fa-database'
]

export default function SectorManager({ sectors, onChanged }) {
  const [editingSector, setEditingSector] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    color: '#dc3545',
    icon: 'fas fa-tag',
    description: '',
    sortOrder: 0,
  })
  const [loading, setLoading] = useState(false)

  const resetForm = () => {
    setFormData({
      name: '',
      color: '#dc3545',
      icon: 'fas fa-tag',
      description: '',
      sortOrder: 0,
    })
    setEditingSector(null)
    setIsCreating(false)
  }

  const handleEdit = (sector) => {
    setEditingSector(sector)
    setIsCreating(false)
    setFormData({
      name: sector.name || '',
      color: sector.color || '#dc3545',
      icon: sector.icon || 'fas fa-tag',
      description: sector.description || '',
      sortOrder: sector.sortOrder || 0,
    })
  }

  const handleCreate = () => {
    resetForm()
    setIsCreating(true)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleColorSelect = (color) => {
    setFormData(prev => ({ ...prev, color }))
  }

  const handleIconSelect = (icon) => {
    setFormData(prev => ({ ...prev, icon }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.name.trim()) {
      toast.error('Il nome del settore e obbligatorio')
      return
    }

    setLoading(true)
    try {
      if (editingSector) {
        await api.put(`/sectors/${editingSector.id}`, formData)
        toast.success('Settore aggiornato')
      } else {
        await api.post('/sectors', formData)
        toast.success('Settore creato')
      }
      resetForm()
      onChanged?.()
    } catch (err) {
      const msg = err.response?.data?.error || 'Errore nel salvataggio'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (sector) => {
    if (!window.confirm(`Eliminare il settore "${sector.name}"? Tutti i turni associati verranno eliminati.`)) {
      return
    }
    try {
      await api.delete(`/sectors/${sector.id}`)
      toast.success('Settore eliminato')
      if (editingSector?.id === sector.id) {
        resetForm()
      }
      onChanged?.()
    } catch (err) {
      const msg = err.response?.data?.error || 'Errore nell\'eliminazione'
      toast.error(msg)
    }
  }

  const handleToggleActive = async (sector) => {
    try {
      await api.put(`/sectors/${sector.id}`, { isActive: !sector.isActive })
      toast.success(sector.isActive ? 'Settore disattivato' : 'Settore attivato')
      onChanged?.()
    } catch (err) {
      toast.error('Errore nel cambio stato')
    }
  }

  return (
    <div className="sector-manager">
      <div className="sector-manager__header">
        <h2><i className="fas fa-cogs" /> Gestione Settori</h2>
        {!isCreating && !editingSector && (
          <button className="btn btn-primary" onClick={handleCreate}>
            <i className="fas fa-plus" /> Nuovo Settore
          </button>
        )}
      </div>

      {(isCreating || editingSector) && (
        <div className="sector-form-card">
          <h3>
            <i className={editingSector ? 'fas fa-edit' : 'fas fa-plus-circle'} />
            {editingSector ? ` Modifica "${editingSector.name}"` : ' Nuovo Settore'}
          </h3>
          <form onSubmit={handleSubmit} className="sector-form">
            <div className="form-row">
              <label htmlFor="name">Nome <span className="req">*</span></label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Es: Regia, Redazione, Tecnico..."
                required
              />
            </div>

            <div className="form-row">
              <label>Colore</label>
              <div className="color-picker">
                {DEFAULT_COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    className={`color-swatch ${formData.color === c ? 'is-selected' : ''}`}
                    style={{ backgroundColor: c }}
                    onClick={() => handleColorSelect(c)}
                    title={c}
                  />
                ))}
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => handleColorSelect(e.target.value)}
                  className="color-input"
                  title="Colore personalizzato"
                />
              </div>
            </div>

            <div className="form-row">
              <label>Icona</label>
              <div className="icon-picker">
                {DEFAULT_ICONS.map(icon => (
                  <button
                    key={icon}
                    type="button"
                    className={`icon-option ${formData.icon === icon ? 'is-selected' : ''}`}
                    onClick={() => handleIconSelect(icon)}
                    title={icon}
                  >
                    <i className={icon} />
                  </button>
                ))}
              </div>
            </div>

            <div className="form-row">
              <label htmlFor="description">Descrizione</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={2}
                placeholder="Descrizione opzionale..."
              />
            </div>

            <div className="form-row">
              <label htmlFor="sortOrder">Ordine</label>
              <input
                type="number"
                id="sortOrder"
                name="sortOrder"
                value={formData.sortOrder}
                onChange={handleChange}
                min="0"
                style={{ width: '100px' }}
              />
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-outline" onClick={resetForm}>
                <i className="fas fa-times" /> Annulla
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <><i className="fas fa-spinner fa-spin" /> Salvataggio...</>
                ) : (
                  <><i className="fas fa-save" /> {editingSector ? 'Aggiorna' : 'Crea'}</>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="sector-list">
        {sectors.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-folder-open" />
            <p>Nessun settore configurato</p>
          </div>
        ) : (
          sectors.map(sector => (
            <div
              key={sector.id}
              className={`sector-item ${!sector.isActive ? 'is-inactive' : ''}`}
            >
              <div className="sector-item__icon" style={{ backgroundColor: sector.color || '#dc3545' }}>
                <i className={sector.icon || 'fas fa-tag'} />
              </div>
              <div className="sector-item__info">
                <strong>{sector.name}</strong>
                {sector.description && <p>{sector.description}</p>}
                {!sector.isActive && <span className="badge badge-muted">Disattivato</span>}
              </div>
              <div className="sector-item__actions">
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  onClick={() => handleToggleActive(sector)}
                  title={sector.isActive ? 'Disattiva' : 'Attiva'}
                >
                  <i className={sector.isActive ? 'fas fa-eye-slash' : 'fas fa-eye'} />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  onClick={() => handleEdit(sector)}
                  title="Modifica"
                >
                  <i className="fas fa-edit" />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost btn-danger"
                  onClick={() => handleDelete(sector)}
                  title="Elimina"
                >
                  <i className="fas fa-trash" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
